type WorkspaceUiControllerOptions = {
  workspaceInsightsContent: HTMLElement | null;
  getProjects: () => any[];
  getVisibleSessions: () => any[];
  getCurrentSessionId: () => string | null;
  getSessionMeta: (sessionId: string) => any;
  findBestProjectForSession: (session: any, projectsById: Map<string, any>) => any;
  buildVirtualProjectFromSession: (session: any) => any;
  decodeClaudeProjectDir: (value: string) => string;
  isSameOrChildPath: (parent: string, child: string) => boolean;
  buildCachedSessionSnapshot: (sessionId: string) => any;
  getMessageDomCount: () => number;
  getUsageText: () => string;
  getSelectedAgent: () => string;
  getCurrentAgent: () => string;
  getCurrentMode: () => string;
  getCurrentModel: () => string;
  getCurrentActiveRuntime: () => any;
  getCurrentRuntimeCount: () => number;
  isCurrentSessionRunning: () => boolean;
  agentLabels: Record<string, string>;
  modeLabels: Record<string, string>;
  escapeHtml: (value: any) => string;
  timeAgo: (value: any) => string;
};

export function createWorkspaceUiController(options: WorkspaceUiControllerOptions) {
  function buildWorkspaceActionButtons(actions: any[], buildOptions: any = {}) {
    const className = buildOptions.compact ? 'workspace-action-row' : 'workspace-action-grid';
    return `
      <div class="${className}">
        ${actions.map((action) => `
          <button
            class="workspace-action-btn${action.primary ? ' primary' : ''}"
            type="button"
            data-workspace-action="${options.escapeHtml(action.action)}"
            ${action.projectId ? `data-project-id="${options.escapeHtml(action.projectId)}"` : ''}
          >${options.escapeHtml(action.label)}</button>
        `).join('')}
      </div>
    `;
  }

  function getCurrentProjectContext() {
    const currentSessionId = options.getCurrentSessionId();
    const currentMeta = currentSessionId ? options.getSessionMeta(currentSessionId) : null;
    if (!currentMeta) return null;
    const projectsById = new Map(options.getProjects().map((project) => [project.id, project]));
    return options.findBestProjectForSession(currentMeta, projectsById) || options.buildVirtualProjectFromSession(currentMeta);
  }

  function sessionBelongsToProject(session: any, project: any, projectsById: Map<string, any>) {
    if (!session || !project) return false;
    if (project.isVirtualCwd) {
      const sessionPath = session.cwd || options.decodeClaudeProjectDir(session.importedFrom);
      return !!sessionPath && options.isSameOrChildPath(project.path, sessionPath);
    }
    const matchedProject = options.findBestProjectForSession(session, projectsById);
    return matchedProject ? matchedProject.id === project.id : false;
  }

  function getCurrentProjectSessionCount(project: any) {
    if (!project) return 0;
    const visibleSessions = options.getVisibleSessions();
    const projectsById = new Map(options.getProjects().map((entry) => [entry.id, entry]));
    return visibleSessions.filter((session) => sessionBelongsToProject(session, project, projectsById)).length;
  }

  function getCurrentMessageCount() {
    const currentSessionId = options.getCurrentSessionId();
    const cachedSnapshot = currentSessionId ? options.buildCachedSessionSnapshot(currentSessionId) : null;
    if (cachedSnapshot?.messages?.length) return cachedSnapshot.messages.length;
    return options.getMessageDomCount();
  }

  function buildWorkspaceInsightsMarkup() {
    const visibleSessions = options.getVisibleSessions();
    const currentSessionId = options.getCurrentSessionId();
    const currentMeta = currentSessionId ? options.getSessionMeta(currentSessionId) : null;
    const activeProject = getCurrentProjectContext();
    const runningCount = visibleSessions.filter((session) => session.isRunning).length;
    const currentMessageCount = getCurrentMessageCount();
    const usageText = options.getUsageText() || (options.getCurrentAgent() === 'codex' ? '暂无 token 统计' : '暂无费用统计');
    const modeLabel = options.modeLabels[options.getCurrentMode()] || options.getCurrentMode();
    const modelLabel = options.getCurrentModel() || (options.getCurrentAgent() === 'codex' ? '默认模型' : 'Default');
    const channelLabel = options.getCurrentActiveRuntime()?.channelLabel || '当前渠道未建立';
    const runtimeCountLabel = options.getCurrentRuntimeCount() > 0 ? `${options.getCurrentRuntimeCount()} 个` : '0 个';
    const selectedAgentLabel = options.agentLabels[options.getSelectedAgent()] || options.getSelectedAgent();
    const currentAgentLabel = options.agentLabels[options.getCurrentAgent()] || options.getCurrentAgent();
    const runtimeLabel = options.isCurrentSessionRunning() ? '运行中' : currentMeta ? '待命中' : '未开始';
    const projectSessionCount = getCurrentProjectSessionCount(activeProject);
    const actionButtons = buildWorkspaceActionButtons([
      { action: 'new-session', label: '新建会话', primary: true },
      { action: 'import-session', label: '导入历史' },
      { action: 'switch-model', label: '切换模型' },
      { action: 'switch-mode', label: '切换模式' },
      ...(activeProject ? [{ action: 'focus-project', label: '定位项目', projectId: activeProject.id }] : []),
      { action: 'open-settings', label: '打开设置' },
    ]);

    return `
      <div class="insights-shell">
        <section class="insights-hero">
          <div class="insights-hero-stats">
            <div class="insights-hero-stat">
              <span>新对话</span>
              <strong>${options.escapeHtml(selectedAgentLabel)}</strong>
            </div>
            <div class="insights-hero-stat">
              <span>会话数</span>
              <strong>${visibleSessions.length}</strong>
            </div>
            <div class="insights-hero-stat">
              <span>运行中</span>
              <strong>${runningCount}</strong>
            </div>
          </div>
        </section>

        <section class="insights-card">
          <div class="insights-card-header">
            <div>
              <div class="insights-card-kicker">当前会话</div>
              <h3>${options.escapeHtml(currentMeta?.title || '还没有打开会话')}</h3>
            </div>
            <span class="insights-status-pill${options.isCurrentSessionRunning() ? ' running' : ''}">${runtimeLabel}</span>
          </div>
          <div class="insights-detail-list">
            <div class="insights-detail-item"><span>当前代理</span><strong>${options.escapeHtml(currentAgentLabel)}</strong></div>
            <div class="insights-detail-item"><span>当前渠道</span><strong>${options.escapeHtml(channelLabel)}</strong></div>
            <div class="insights-detail-item"><span>模型</span><strong>${options.escapeHtml(modelLabel)}</strong></div>
            <div class="insights-detail-item"><span>模式</span><strong>${options.escapeHtml(modeLabel)}</strong></div>
            <div class="insights-detail-item"><span>子线程</span><strong>${options.escapeHtml(runtimeCountLabel)}</strong></div>
            <div class="insights-detail-item"><span>消息数</span><strong>${currentMessageCount > 0 ? `${currentMessageCount} 条` : '暂无'}</strong></div>
            <div class="insights-detail-item"><span>最近更新</span><strong>${options.escapeHtml(currentMeta?.updated ? options.timeAgo(currentMeta.updated) : '尚未开始')}</strong></div>
          </div>
          <div class="insights-note">${options.escapeHtml(usageText)}</div>
        </section>

        <section class="insights-card">
          <div class="insights-card-header">
            <div>
              <div class="insights-card-kicker">快捷操作</div>
            </div>
          </div>
          ${actionButtons}
        </section>
      </div>
    `;
  }

  function renderWorkspaceInsights() {
    if (!options.workspaceInsightsContent) return;
    options.workspaceInsightsContent.innerHTML = buildWorkspaceInsightsMarkup();
  }

  function buildWelcomeMarkup(agent: string) {
    const label = options.agentLabels[agent] || options.agentLabels.claude;
    const sessionCount = options.getVisibleSessions().length;
    const projectCount = options.getProjects().length;
    return `
      <div class="welcome-msg">
        <div class="welcome-header">
          <div class="welcome-icon">✦</div>
          <h3>${label} 工作区</h3>
        </div>
        <div class="welcome-stats">
          <div class="welcome-stat">
            <strong>${sessionCount}</strong>
            <span>会话数</span>
          </div>
          <div class="welcome-stat">
            <strong>${projectCount}</strong>
            <span>项目数</span>
          </div>
          <div class="welcome-stat">
            <strong>${options.modeLabels[options.getCurrentMode()] || options.getCurrentMode()}</strong>
            <span>模式</span>
          </div>
        </div>
        <div class="welcome-actions">
          ${buildWorkspaceActionButtons([
            { action: 'new-session', label: '新建会话', primary: true },
            { action: 'import-session', label: '导入历史' },
            { action: 'switch-model', label: '切换模型' },
          ], { compact: true })}
        </div>
        <div class="welcome-panels">
          <section class="welcome-panel">
            <div class="welcome-panel-kicker">常用指令</div>
            <ul class="welcome-list">
              <li><code>/model</code> 查看或切换模型</li>
              <li><code>/mode</code> 切换权限模式</li>
              <li><code>/compact</code> 压缩上下文</li>
            </ul>
          </section>
          <section class="welcome-panel">
            <div class="welcome-panel-kicker">多模态协作</div>
            <ul class="welcome-list">
              <li>支持随消息附带图片，适合 UI、截图和报错定位。</li>
            </ul>
          </section>
        </div>
      </div>
    `;
  }

  return {
    buildWorkspaceActionButtons,
    getCurrentProjectContext,
    getCurrentProjectSessionCount,
    buildWelcomeMarkup,
    renderWorkspaceInsights,
  };
}
